/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { styled } from "@mui/material";
import { Typography, Checkbox, Chip, Button } from "@mui/material";

export const StyledSectionHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  flexWrap: 'wrap',
})

export const StyledSectionActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
})

export const StyledCounter = styled(Typography)({
  fontSize: '12px',
  fontWeight: '400',
  color: '#6b6b6b',
})

export const StyledLinkButton = styled(Button)({
  minWidth: 'auto',
  padding: '2px 6px',
  fontSize: '12px',
  fontWeight: '500',
  textTransform: 'none',
  color: '#1976d2',
  '&.Mui-disabled': {
    color: '#bdbdbd',
  },
})

// auto-fit rather than a fixed two-column rule: the drawer is a fixed width today, but a group
// card stays readable instead of crushing if that ever narrows.
export const StyledGroupGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: '12px',
  marginTop: '10px',
})

export const StyledGroupCard = styled('div')({
  border: '1px solid #e0e0e0',
  borderRadius: '6px',
  padding: '10px 12px',
  backgroundColor: '#fff',
})

export const StyledGroupHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '4px',
})

// #6b6b6b at 12px clears WCAG AA for normal text (5.33:1 on white); the #8a8a8a/11px this
// started as measured 3.45:1, below the 4.5:1 floor.
export const StyledGroupLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: '600',
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: '#6b6b6b',
})

export const StyledModuleRow = styled('label', {
  // `disabled` is not a valid attribute on a <label>; it only drives the styling here.
  shouldForwardProp: (prop) => prop !== 'disabled',
})(({ disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  minWidth: 0,
}))

export const StyledCheckbox = styled(Checkbox)({
  padding: '4px',
  color: '#c4c4c4',
  '&.Mui-checked': {
    color: '#ff4c00',
  },
  '&.MuiCheckbox-indeterminate': {
    color: '#ff4c00',
  },
})

// The label is the only element that may shrink, so a long module name ellipsises instead of
// pushing the tag that explains a blocked row out of the card.
// `struck` marks one of the profile's own modules that has been unticked: it stays legible rather
// than vanishing, so what the profile offered is still visible. #767676 clears WCAG AA on white
// (4.54:1); the lighter grey this reads as at a glance would not.
export const StyledModuleLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'struck',
})(({ struck }) => ({
  fontSize: '13px',
  fontWeight: '400',
  color: struck ? '#767676' : '#3c3c3c',
  textDecoration: struck ? 'line-through' : 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
}))

export const StyledBlockedTag = styled(Chip)({
  height: '17px',
  flexShrink: 0,
  fontSize: '10px',
  fontWeight: '600',
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  backgroundColor: '#eef2f7',
  color: '#5a6b80',
  '& .MuiChip-label': {
    padding: '0px 5px',
  },
})

export const StyledDivergenceTag = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'tone',
})(({ tone }) => ({
  height: '17px',
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: '700',
  backgroundColor: tone === 'added' ? '#fdf0e8' : '#f1f1f1',
  color: tone === 'added' ? '#8a3d00' : '#5a5a5a',
  '& .MuiChip-label': {
    padding: '0px 5px',
  },
}))

export const StyledHelperMessage = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'tone',
})(({ tone }) => ({
  fontSize: '12px',
  fontWeight: '400',
  marginTop: '8px',
  color: tone === 'warning' ? '#a45b00' : '#6b6b6b',
}))
